import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Save } from 'lucide-react';
import {
  PageHeader,
  TextArea,
  TextInput,
  Select,
  LoadingState,
  ErrorState,
} from '@/components/common';
import {
  appVersionPoliciesApi,
  type AppVersionPolicy,
  type AppVersionPolicyAppType,
  type AppVersionPolicyPlatform,
  type AppVersionPolicyUpdateType,
  type SaveAppVersionPolicyInput,
} from '@/services/api';

type PolicyKey = `${AppVersionPolicyAppType}:${AppVersionPolicyPlatform}`;

const APP_TYPE_LABEL: Record<AppVersionPolicyAppType, string> = {
  customer: '고객앱',
  business: '비즈앱',
};

const PLATFORM_LABEL: Record<AppVersionPolicyPlatform, string> = {
  android: 'Android',
  ios: 'iOS',
};

const UPDATE_TYPE_LABEL: Record<AppVersionPolicyUpdateType, string> = {
  none: '차단 없음',
  recommend: '권장 업데이트',
  force: '강제 업데이트',
};

const POLICY_TARGETS: Array<{
  appType: AppVersionPolicyAppType;
  platform: AppVersionPolicyPlatform;
  storeUrl: string;
}> = [
  {
    appType: 'customer',
    platform: 'android',
    storeUrl: 'https://play.google.com/store/apps/details?id=com.mungtoday.app',
  },
  {
    appType: 'customer',
    platform: 'ios',
    storeUrl: 'https://apps.apple.com/app/mungtoday',
  },
  {
    appType: 'business',
    platform: 'android',
    storeUrl: 'https://play.google.com/store/apps/details?id=com.mungtoday.store',
  },
  {
    appType: 'business',
    platform: 'ios',
    storeUrl: 'https://apps.apple.com/app/mungstore',
  },
];

const toPolicyKey = (
  appType: AppVersionPolicyAppType,
  platform: AppVersionPolicyPlatform,
): PolicyKey => `${appType}:${platform}`;

const blankToNull = (value: string | null | undefined) => {
  const nextValue = value?.trim();
  return nextValue ? nextValue : null;
};

const createDefaultForm = (
  appType: AppVersionPolicyAppType,
  platform: AppVersionPolicyPlatform,
  storeUrl: string,
): SaveAppVersionPolicyInput => ({
  appType,
  platform,
  updateType: 'none',
  minVersion: null,
  latestVersion: null,
  storeUrl,
  fallbackStoreUrl: storeUrl,
  title: '앱 업데이트가 필요해요',
  message: '안정적인 서비스 이용을 위해 최신 버전으로 업데이트해 주세요.',
  enabled: true,
});

const createInitialForms = () =>
  POLICY_TARGETS.reduce(
    (acc, target) => {
      acc[toPolicyKey(target.appType, target.platform)] = createDefaultForm(
        target.appType,
        target.platform,
        target.storeUrl,
      );
      return acc;
    },
    {} as Record<PolicyKey, SaveAppVersionPolicyInput>,
  );

const mergePolicyToForm = (
  policy: AppVersionPolicy,
  fallback: SaveAppVersionPolicyInput,
): SaveAppVersionPolicyInput => ({
  appType: policy.appType,
  platform: policy.platform,
  minVersion: policy.minVersion ?? null,
  latestVersion: policy.latestVersion ?? null,
  updateType: policy.updateType,
  storeUrl: policy.storeUrl ?? fallback.storeUrl,
  fallbackStoreUrl: policy.fallbackStoreUrl ?? fallback.fallbackStoreUrl,
  title: policy.title ?? fallback.title,
  message: policy.message ?? fallback.message,
  enabled: policy.enabled ?? true,
});

const toSaveInput = (
  form: SaveAppVersionPolicyInput,
): SaveAppVersionPolicyInput => ({
  ...form,
  minVersion: blankToNull(form.minVersion),
  latestVersion: blankToNull(form.latestVersion),
  storeUrl: blankToNull(form.storeUrl),
  fallbackStoreUrl: blankToNull(form.fallbackStoreUrl),
  title: blankToNull(form.title),
  message: blankToNull(form.message),
});

export function AppVersionPolicyPage() {
  const queryClient = useQueryClient();
  const [forms, setForms] = useState<Record<PolicyKey, SaveAppVersionPolicyInput>>(
    createInitialForms,
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['app-version-policies'],
    queryFn: appVersionPoliciesApi.getPolicies,
  });

  const policiesByKey = useMemo(() => {
    const map = new Map<PolicyKey, AppVersionPolicy>();
    data?.forEach(policy => {
      map.set(toPolicyKey(policy.appType, policy.platform), policy);
    });
    return map;
  }, [data]);

  useEffect(() => {
    if (!data) return;

    setForms(currentForms => {
      const nextForms = { ...currentForms };
      data.forEach(policy => {
        const key = toPolicyKey(policy.appType, policy.platform);
        nextForms[key] = mergePolicyToForm(policy, currentForms[key]);
      });
      return nextForms;
    });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: appVersionPoliciesApi.savePolicy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-version-policies'] });
    },
    onError: err => {
      alert(err instanceof Error ? err.message : '앱 버전 정책 저장에 실패했습니다.');
    },
  });

  const updateForm = (
    key: PolicyKey,
    patch: Partial<SaveAppVersionPolicyInput>,
  ) => {
    setForms(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...patch,
      },
    }));
  };

  const resetForm = (key: PolicyKey) => {
    const target = POLICY_TARGETS.find(item => toPolicyKey(item.appType, item.platform) === key);
    if (!target) return;

    const policy = policiesByKey.get(key);
    const fallback = createDefaultForm(target.appType, target.platform, target.storeUrl);
    setForms(prev => ({
      ...prev,
      [key]: policy ? mergePolicyToForm(policy, fallback) : fallback,
    }));
  };

  const saveForm = (key: PolicyKey) => {
    saveMutation.mutate(toSaveInput(forms[key]));
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="앱 버전 정책"
        subtitle="고객앱/비즈앱과 Android/iOS 조합별 강제 업데이트 정책을 관리합니다."
      />

      <div className="grid gap-4 xl:grid-cols-2">
        {POLICY_TARGETS.map(target => {
          const key = toPolicyKey(target.appType, target.platform);
          const form = forms[key];
          const policy = policiesByKey.get(key);
          const saving = saveMutation.isPending;

          return (
            <section
              key={key}
              className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    {APP_TYPE_LABEL[target.appType]} · {PLATFORM_LABEL[target.platform]}
                  </h2>
                  <p className="mt-1 text-xs text-gray-500">
                    {policy?.policySeq ? `정책 번호 ${policy.policySeq}` : '아직 저장된 정책 없음'}
                  </p>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.enabled ?? true}
                    onChange={event => updateForm(key, { enabled: event.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  사용
                </label>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <label className="space-y-1.5">
                  <span className="text-xs font-medium text-gray-600">정책</span>
                  <Select
                    value={form.updateType}
                    onChange={event =>
                      updateForm(key, {
                        updateType: event.target.value as AppVersionPolicyUpdateType,
                      })
                    }
                  >
                    {Object.entries(UPDATE_TYPE_LABEL).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </label>

                <label className="space-y-1.5">
                  <span className="text-xs font-medium text-gray-600">강제 기준 버전</span>
                  <TextInput
                    placeholder="예: 0.9.5"
                    value={form.minVersion ?? ''}
                    onChange={event => updateForm(key, { minVersion: event.target.value })}
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-xs font-medium text-gray-600">권장 기준 버전</span>
                  <TextInput
                    placeholder="예: 0.9.7"
                    value={form.latestVersion ?? ''}
                    onChange={event => updateForm(key, { latestVersion: event.target.value })}
                  />
                </label>

                <label className="space-y-1.5 md:col-span-2">
                  <span className="text-xs font-medium text-gray-600">스토어 URL</span>
                  <TextInput
                    value={form.storeUrl ?? ''}
                    onChange={event => updateForm(key, { storeUrl: event.target.value })}
                  />
                </label>

                <label className="space-y-1.5 md:col-span-2">
                  <span className="text-xs font-medium text-gray-600">대체 URL</span>
                  <TextInput
                    value={form.fallbackStoreUrl ?? ''}
                    onChange={event => updateForm(key, { fallbackStoreUrl: event.target.value })}
                  />
                </label>

                <label className="space-y-1.5 md:col-span-2">
                  <span className="text-xs font-medium text-gray-600">제목</span>
                  <TextInput
                    value={form.title ?? ''}
                    onChange={event => updateForm(key, { title: event.target.value })}
                  />
                </label>

                <label className="space-y-1.5 md:col-span-2">
                  <span className="text-xs font-medium text-gray-600">안내 문구</span>
                  <TextArea
                    rows={3}
                    value={form.message ?? ''}
                    onChange={event => updateForm(key, { message: event.target.value })}
                  />
                </label>
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => resetForm(key)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <RotateCcw size={16} />
                  되돌리기
                </button>
                <button
                  type="button"
                  onClick={() => saveForm(key)}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-primary-300"
                >
                  <Save size={16} />
                  저장
                </button>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
